import { Gift, TrendingUp, Shield, Users, Star, ChevronRight, ArrowRight } from 'lucide-react';

interface FunderLandingPageProps {
  onGetStarted: () => void;
}

export function FunderLandingPage({ onGetStarted }: FunderLandingPageProps) {
  return (
    <div className="min-h-screen">

      {/* Hero */}
      <section
        className="relative min-h-[88vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/92 via-gray-900/75 to-gray-900/30" />
        <div className="relative z-10 container mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <span className="inline-block bg-rose-500/20 text-rose-300 text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full border border-rose-500/30 mb-6">
              FAM Funders
            </span>
            <h1 className="text-5xl sm:text-6xl font-extrabold text-white leading-tight mb-6">
              Own the paper
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400">
                around you.
              </span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-xl">
              Gift to someone you care about — or invest for fractional LLC ownership in everyday vehicles.
              Community financing, done right.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onGetStarted}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold text-base px-8 py-4 rounded-full shadow-xl hover:shadow-rose-500/30 hover:scale-105 transition-all"
              >
                Start Funding <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="/campaigns"
                className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold text-base px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-all"
              >
                See Live Campaigns
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Two paths — Gift vs Invest */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Two ways to be a FAM Funder</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Whether you want to give a gift or build a portfolio, FAM makes it seamless — and keeps you anonymous by default.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

            {/* Gift card */}
            <div className="relative rounded-3xl overflow-hidden border border-rose-100 bg-gradient-to-br from-rose-50 to-orange-50 p-8">
              <div className="w-12 h-12 bg-rose-100 rounded-2xl flex items-center justify-center mb-5">
                <Gift className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Gift</h3>
              <p className="text-gray-600 mb-5 leading-relaxed">
                Give any amount to help someone in your circle get their car. No return expected. The creator sees the total raised — not whether you gifted or invested.
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-6">
                {[
                  'Appear by name or privately — your choice',
                  'Any amount, starting at $5',
                  'Reveal your gift anytime to forgive debt',
                  'Notified when campaign funds',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-rose-200 flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-3 h-3 text-rose-700" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={onGetStarted}
                className="w-full bg-gradient-to-r from-rose-500 to-orange-500 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
              >
                Gift to a Campaign
              </button>
            </div>

            {/* Invest card */}
            <div className="relative rounded-3xl overflow-hidden border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
              <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center mb-5">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Invest</h3>
              <p className="text-gray-600 mb-5 leading-relaxed">
                Put money into a campaign and receive fractional LLC ownership of the funded vehicle. Your stake earns returns. Your identity stays private — you appear only as an investor.
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-6">
                {[
                  'Fractional LLC membership certificate',
                  'Vote on vehicle management',
                  'Earn on appreciation or rental income',
                  'Build an anonymous vehicle portfolio',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-3 h-3 text-indigo-700" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={onGetStarted}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
              >
                Invest in a Vehicle
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The mechanic */}
      <section className="py-20 bg-gray-950 text-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">The creator sees the goal. Not the split.</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              The creator knows how much has been raised — not whether you gifted or invested. This keeps everyone motivated and relationships intact.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'No gift vs invest pressure',
                desc: "The creator can't see the split, so there's no awkward dynamic. You give on your terms.",
              },
              {
                icon: Users,
                title: 'You choose how you appear',
                desc: 'Show your name on the campaign page, or opt to appear privately. Your call.',
              },
              {
                icon: Star,
                title: 'Grace is yours to give',
                desc: "As a gifter, you can disclose at any time to forgive that portion of the creator's balance. Whenever you're ready.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="w-10 h-10 bg-rose-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-rose-400" />
                </div>
                <h3 className="font-bold text-white mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Power FAMs callout */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-rose-50">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full border border-amber-200 mb-5">
            Power FAMs
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
            Groups fund better together
          </h2>
          <p className="text-gray-600 leading-relaxed mb-6 max-w-xl mx-auto">
            A Power FAM is a crew — families, friend circles, a church group, a sports team — pooling contributions to back a vehicle together.
          </p>
          <div className="inline-block bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-8 text-left max-w-md mx-auto">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-1">Coming Soon</p>
            <p className="text-sm text-amber-800 leading-relaxed">
              Group creation, investment clubs, susus, family support pools, and managed group accounts — Power FAM infrastructure is in development.
            </p>
          </div>
          <br />
          <a
            href="/campaigns"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-rose-500 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:scale-105 transition-transform"
          >
            Find a Campaign to Join <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* FAM News teaser */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-gray-900">FAM News Weekly</h2>
            <a href="/news" className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1">
              All posts <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          <a
            href="/news"
            className="block group bg-gradient-to-br from-gray-950 to-gray-800 rounded-2xl overflow-hidden hover:scale-[1.01] transition-transform"
          >
            <div className="p-8">
              <span className="text-xs text-rose-400 font-semibold uppercase tracking-widest">Issue #1</span>
              <h3 className="text-xl font-bold text-white mt-2 mb-3 group-hover:text-rose-300 transition-colors">
                Own the Paper Around You — Anonymously
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Most people don't realize they can hold fractional stakes in everyday vehicles — the cars driven by the people they know. FAM makes it possible, and keeps your identity private while you do it.
              </p>
              <span className="inline-flex items-center gap-1 text-rose-400 text-sm font-medium mt-5">
                Read the post <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative py-24 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/80 to-gray-950/60" />
        <div className="relative z-10 container mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Ready to become a FAM Funder?</h2>
          <p className="text-gray-300 mb-8 max-w-md mx-auto">
            Browse live campaigns, pick one that matters to you, and contribute — anonymously.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/campaigns"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold px-10 py-4 rounded-full shadow-xl hover:scale-105 transition-transform"
            >
              Browse Campaigns <ArrowRight className="w-5 h-5" />
            </a>
            <button
              onClick={onGetStarted}
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-10 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-all"
            >
              Create Account
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
